import { clsx } from 'clsx'
import TherapistAvatar from './TherapistAvatar'
import type { Therapist, TherapistStatus } from '../../stores/chatStore'

interface TypingIndicatorProps {
  therapist?: Therapist | null
  status: TherapistStatus
}

// Telegram-style status messages (technical statuses only - character status handled in ChatHeader)
const statusMessages: Record<TherapistStatus, string> = {
  idle: '',
  typing: 'печатает...',
  recording_audio: 'записывает аудио...',
  recording_video: 'записывает видео...',
  listening_audio: 'слушает аудио...',
  watching_video: 'смотрит видео...',
  analyzing_photo: 'рассматривает фото...',
  generating_voice: 'записывает голосовое...',
  character: '',  // Character status is not shown in typing indicator
}

// Icons for different statuses
const StatusIcon = ({ status }: { status: TherapistStatus }) => {
  switch (status) {
    case 'recording_audio':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
        </svg>
      )
    case 'recording_video':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
        </svg>
      )
    case 'listening_audio':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
        </svg>
      )
    case 'watching_video':
    case 'analyzing_photo':
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      )
    default:
      return null
  }
}

export default function TypingIndicator({ therapist, status }: TypingIndicatorProps) {
  // Don't render if idle or character status (character is shown in header only)
  if (status === 'idle' || status === 'character') {
    return null
  }

  const type = therapist?.type || 'wendy'

  const bubbleStyle = {
    wendy: 'bubble-wendy',
    artem: 'bubble-artem',
    finch: 'bubble-finch',
    una: 'bubble-una',
  }[type] || 'bubble-assistant'

  const dotColor = {
    wendy: 'bg-wendy',
    artem: 'bg-artem',
    finch: 'bg-finch',
    una: 'bg-una',
  }[type] || 'bg-neutral-400'

  const textColor = {
    wendy: 'text-wendy',
    artem: 'text-artem',
    finch: 'text-finch',
    una: 'text-una',
  }[type] || 'text-neutral-400'

  return (
    <div className="flex items-end gap-2 animate-fade-in">
      <TherapistAvatar therapist={therapist} size="sm" />

      <div className={clsx('px-4 py-3 rounded-3xl rounded-bl-lg', bubbleStyle)} role="status" aria-live="polite" aria-label={statusMessages[status]}>
        <div className="flex items-center gap-2">
          {/* Status icon or typing dots */}
          {status === 'typing' ? (
            <div className="flex items-center gap-1.5" aria-hidden="true">
              <span
                className={clsx('w-2 h-2 rounded-full typing-dot', dotColor)}
                style={{ animationDelay: '0s' }}
              />
              <span
                className={clsx('w-2 h-2 rounded-full typing-dot', dotColor)}
                style={{ animationDelay: '0.2s' }}
              />
              <span
                className={clsx('w-2 h-2 rounded-full typing-dot', dotColor)}
                style={{ animationDelay: '0.4s' }}
              />
            </div>
          ) : (
            <span className={clsx('animate-pulse', textColor)}>
              <StatusIcon status={status} />
            </span>
          )}

          {/* Status text */}
          <span className={clsx('text-sm', textColor)}>
            {statusMessages[status]}
          </span>
        </div>
      </div>
    </div>
  )
}
